import { useNavigate } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { downloadReport, useMarkNotificationsRead, useNotifications } from '@/lib/admin-queries';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMe } from '@/lib/queries';
import { cn } from '@/lib/utils';

/**
 * In-app notifications. Only admins have anything to be notified about today (reports), so the
 * bell stays out of a regular user's way rather than sitting there permanently empty.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isAdmin = me?.role === 'ADMIN';
  const { data } = useNotifications(isAdmin);
  const markRead = useMarkNotificationsRead();

  if (!isAdmin) return null;
  const unread = data?.unread ?? 0;

  return (
    <Popover
      onOpenChange={(open) => {
        if (open && unread > 0) markRead.mutate(undefined);
      }}
    >
      <PopoverTrigger
        aria-label={
          unread > 0 ? t('notifications.unread', { count: unread }) : t('notifications.title')
        }
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent"
      >
        <Bell className="h-4 w-4" aria-hidden="true" />
        {unread > 0 ? (
          <span
            aria-hidden="true"
            className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary ring-2 ring-background"
          />
        ) : null}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="end">
        <h2 className="px-2 py-1 text-sm font-medium">{t('notifications.title')}</h2>
        <ul className="max-h-80 overflow-y-auto">
          {(data?.data ?? []).map((note) => {
            const payload = note.payload as { reportId?: string; title?: string; message?: string };
            const failed = note.type === 'report.failed';
            return (
              <li
                key={note.id}
                className={cn('rounded-md px-2 py-2', !note.readAt && 'bg-accent/50')}
              >
                <p className="text-sm">
                  {failed
                    ? t('notifications.reportFailed', { title: payload.title ?? '' })
                    : t('notifications.reportReady', { title: payload.title ?? '' })}
                </p>
                {failed && payload.message ? (
                  <p className="text-xs text-destructive">{payload.message}</p>
                ) : null}
                <p className="text-xs text-muted-foreground">
                  {new Date(note.createdAt).toLocaleString()}
                </p>
                {!failed && payload.reportId ? (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => downloadReport(payload.reportId!)}
                  >
                    {t('notifications.download')}
                  </Button>
                ) : null}
              </li>
            );
          })}
          {(data?.data.length ?? 0) === 0 ? (
            <li className="px-2 py-6 text-center text-sm text-muted-foreground">
              {t('notifications.empty')}
            </li>
          ) : null}
        </ul>
        <Button
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => void navigate({ to: '/admin/reports', search: {} })}
        >
          {t('notifications.allReports')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
